import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import { Badge, Tooltip } from "@coval/ui"

import type {
  MetricRecordData,
  TargetCondition,
} from "@/types/metrics/contracts"

import {
  getMetricColor,
  getMetricColorClass,
  getMetricColorForMetric,
} from "../utils/getMetricColor"
import { yesNoBadgeShapeClasses } from "../utils/yesNoBadgeClasses"

const normalizeYesNoValue = (value: number | null | undefined): -1 | 0 | 1 => {
  if (value === 1 || value === 0) {
    return value
  }
  return -1
}

/**
 * Derive "YES" | "NO" | "UNKNOWN" from a MetricOutput-like object.
 * Prefers string_value (canonical for output_type "string") and falls back
 * to float_value for legacy metric outputs.
 */
export const deriveYesNoString = (mo: {
  float_value?: number | null
  string_value?: string | null
}): "YES" | "NO" | "UNKNOWN" => {
  const sv = mo.string_value?.toUpperCase() ?? null
  if (
    sv === ANNOTATION_VALUE.YES ||
    sv === ANNOTATION_VALUE.NO ||
    sv === ANNOTATION_VALUE.UNKNOWN
  ) {
    return sv
  }
  // Fallback to float_value for legacy metric outputs
  if (mo.float_value != null) {
    const n = normalizeYesNoValue(mo.float_value)
    return n === 1
      ? ANNOTATION_VALUE.YES
      : n === 0
        ? ANNOTATION_VALUE.NO
        : ANNOTATION_VALUE.UNKNOWN
  }
  return ANNOTATION_VALUE.UNKNOWN
}

/** Override labels for the YES/NO/UNKNOWN slots. */
export interface YesNoLabels {
  yes?: string
  no?: string
  unknown?: string
}

export const StringValueYesNoChip = ({
  value,
  mo,
  targetCondition,
  showDash = false,
  labels,
  metric,
}: {
  value?: string | null
  mo?: { float_value?: number | null; string_value?: string | null }
  targetCondition?: TargetCondition
  showDash?: boolean
  labels?: YesNoLabels
  metric?: MetricRecordData
}) => {
  const resolvedValue = mo ? deriveYesNoString(mo) : value

  if (resolvedValue === null || resolvedValue === undefined) {
    return null
  }

  if (
    resolvedValue !== ANNOTATION_VALUE.YES &&
    resolvedValue !== ANNOTATION_VALUE.NO &&
    resolvedValue !== ANNOTATION_VALUE.UNKNOWN
  ) {
    return <div>N/A</div>
  }

  const color = metric
    ? getMetricColorForMetric(resolvedValue, metric)
    : getMetricColor(resolvedValue, targetCondition)
  const colorClass = getMetricColorClass(color)
  const yesLabel = labels?.yes ?? "Yes"
  const noLabel = labels?.no ?? "No"
  const unknownLabel = labels?.unknown ?? "N/A"
  return (
    <div className="flex items-center justify-center h-full">
      {resolvedValue === ANNOTATION_VALUE.UNKNOWN &&
        (showDash ? (
          <Badge size="chip" className={`h-[28px] text-sm ${colorClass}`}>
            --
          </Badge>
        ) : (
          <Tooltip content={unknownLabel}>
            <Badge size="chip" className={`h-[28px] text-sm ${colorClass}`}>
              {unknownLabel}
            </Badge>
          </Tooltip>
        ))}
      {resolvedValue === ANNOTATION_VALUE.YES && (
        <Tooltip content={yesLabel}>
          <Badge
            size="chip"
            className={`${yesNoBadgeShapeClasses} ${colorClass}`}
          >
            {yesLabel}
          </Badge>
        </Tooltip>
      )}
      {resolvedValue === ANNOTATION_VALUE.NO && (
        <Tooltip content={noLabel}>
          <Badge
            size="chip"
            className={`${yesNoBadgeShapeClasses} ${colorClass}`}
          >
            {noLabel}
          </Badge>
        </Tooltip>
      )}
    </div>
  )
}
